import { ClassValue } from 'clsx';
import { UseFormRegisterReturn } from 'react-hook-form';

import { cn } from '@/utils/styles';

import { colors, sizes, lines as textareaLines } from './constants';
import { TextAreaColor, TextAreaLines, TextAreaSize } from './types';
import { Typography } from '../typography';
import { useState } from 'react';

export type TextAreaProps = {
  readonly id?: string;
  readonly size?: TextAreaSize;
  readonly color?: TextAreaColor;
  readonly lines?: TextAreaLines;
  readonly resize?: boolean;
  readonly maxLength?: number;
  readonly placeholder?: string;
  readonly autoComplete?: string;
  readonly registration?: Partial<UseFormRegisterReturn>;
  readonly className?: ClassValue;
  readonly minLength?: number;
  readonly disabled?: boolean;
};

export function TextArea({
  id,
  size = 'md',
  color = 'primary',
  lines = 2,
  resize = true,
  maxLength,
  placeholder,
  autoComplete = 'off',
  registration,
  className,
  minLength,
  disabled = false
}: TextAreaProps) {
  const [currentLength, setCurrentLength] = useState(0);
  return (
    <>
      <textarea
        disabled={disabled}
        id={id}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={cn(
          textareaLines[lines],
          'min-h-[40px] w-full transition',
          resize ? 'max-h-[50vh]' : 'resize-none',
          'placeholder:text-base-secondary text-base-primary font-normal sm:text-base sm:leading-6',
          sizes[size],
          'appearance-none rounded-sm border outline-none',
          'focus:outline-none',
          'focus:ring-2 focus:ring-offset-0',
          colors[color],
          'disabled:bg-neutral-25 disabled:border-neutral-50',
          'disabled:text-neutral-disabled disabled:placeholder:text-neutral-disabled',
          'disabled:cursor-not-allowed',
          className
        )}
        {...registration}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
          const value = event.target.value ?? '';
          setCurrentLength(value.length);
          registration?.onChange?.(event);
        }}
      />
      {maxLength && (
        <Typography
          as="p"
          color={currentLength > maxLength ? 'error' : 'secondary'}
          size="body-3"
          weight="normal"
          className="italic"
        >
          {`${currentLength}/${maxLength} characters. Min ${minLength}.`}
          {currentLength > maxLength && (
            <span className="text-error-500 translate-x-0 translate-z-0 text-base">
              {`Max length of ${maxLength} characters.`}
            </span>
          )}
        </Typography>
      )}
    </>
  );
}
